import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BookOpen, CheckSquare, Users, GraduationCap, ShieldCheck, ChevronRight, Video, Clapperboard, HardDrive } from 'lucide-react';
import CurriculumTab from './certification/CurriculumTab';
import ExamSettingsTab from './certification/ExamSettingsTab';
import PartnersTab from './certification/PartnersTab';
import TutorialsTab from './certification/TutorialsTab';
import VideoTasksTab from './certification/VideoTasksTab';
import LibraryTab from './certification/LibraryTab';

const TABS = [
  { id: 'curriculum', label: 'Curriculum', icon: BookOpen },
  { id: 'exam', label: 'Certifications', icon: CheckSquare },
  { id: 'partners', label: 'Partners', icon: Users },
  { id: 'tutorials', label: 'Tutorials', icon: Video },
  { id: 'video-tasks', label: 'Video tasks', icon: Clapperboard },
  { id: 'library', label: 'Library', icon: HardDrive },
] as const;

type TabId = (typeof TABS)[number]['id'];

/**
 * The DEFAULT certification id used for the affiliate detail lookup
 * (AffiliateDetailPage.tsx) — no longer the only certification. Multiple
 * certifications now exist; which one actually activates a partner's
 * referral link is server-decided (`activatesPartner`, exclusive across
 * tracks) and can differ from this default.
 */
export const TRACK_ID = 'affiliate-partner-certification';

export default function CertificationPortal() {
  // `?tab=tutorials` deep-links a tab (the help menu's "Tutorials" row);
  // `?tab=video-tasks&task=asg_…` is the admin email's link to one task;
  // `?tab=library` opens the video library.
  const [params] = useSearchParams();
  const [tab, setTab] = useState<TabId>(() => {
    const t = params.get('tab');
    return TABS.some((x) => x.id === t) ? (t as TabId) : 'curriculum';
  });

  // Same control-banner shape as the Command Center, in black and white.
  const tabButton = (id: TabId, label: string, Icon: typeof BookOpen) => (
    <button
      key={id}
      type="button"
      onClick={() => setTab(id)}
      aria-selected={tab === id}
      role="tab"
      className={`flex items-center gap-2 px-4 min-h-[44px] rounded-lg text-sm font-medium transition-all ${
        tab === id
          ? 'bg-white text-gray-900 shadow-sm'
          : 'text-white/70 hover:text-white hover:bg-white/10'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );

  return (
    <div className="min-h-screen -m-3 md:-m-6 bg-white">
      {/* Top control banner — full width */}
      <div className="bg-black text-white px-6 py-5 shadow-lg">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-6 min-w-0">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center relative flex-shrink-0">
                <GraduationCap size={22} className="text-white" />
                <div className="absolute -bottom-1.5 -right-1.5 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                  <ShieldCheck size={14} className="text-white" />
                </div>
              </div>
              <div className="min-w-0">
                <nav className="flex items-center gap-1 text-[11px] text-white/40 mb-0.5">
                  <Link to="/affiliates" className="hover:text-white/80 transition-colors">
                    Affiliates
                  </Link>
                  <ChevronRight className="w-3 h-3" />
                  <span className="text-white/60">Certification</span>
                </nav>
                <h1 className="text-xl font-bold text-white truncate">Certification Portal</h1>
                <p className="text-white/50 text-xs mt-1">
                  Record the pitch once. Every partner learns it before their link goes live.
                </p>
              </div>
            </div>

            {/* View tabs — desktop */}
            <div className="hidden md:flex items-center bg-white/10 rounded-xl p-1 ml-4" role="tablist">
              {TABS.map(({ id, label, icon: Icon }) => tabButton(id, label, Icon))}
            </div>
          </div>
        </div>

        {/* View tabs — mobile */}
        <div className="md:hidden flex items-center bg-white/10 rounded-xl p-1 mt-4 overflow-x-auto" role="tablist">
          {TABS.map(({ id, label, icon: Icon }) => tabButton(id, label, Icon))}
        </div>
      </div>

      <div className="p-4 md:p-6">
        {tab === 'curriculum' && <CurriculumTab />}
        {tab === 'exam' && <ExamSettingsTab />}
        {tab === 'partners' && <PartnersTab trackId={TRACK_ID} />}
        {tab === 'tutorials' && <TutorialsTab />}
        {tab === 'video-tasks' && <VideoTasksTab />}
        {tab === 'library' && <LibraryTab />}
      </div>
    </div>
  );
}
